/**
 * Esoteria Music Manager
 *
 * Higher-level wrapper around the low-level `MusicPlayer` that:
 *   1. Picks this playthrough's level->song table once, at boot
 *   2. Wires gameplay events (level entry, threat level) to (level, intensity)
 *   3. Provides a single singleton-friendly API for the main game loop
 *   4. Handles graceful autoplay-policy deferral until the first user gesture
 *
 * The addressing model is the original engine's — (level 0..9, intensity 0..2)
 * and nothing else. See `music-states.ts` for where each table came from and
 * `wiki/music-system-reverse-engineering.md` for the disassembly.
 *
 * See also:
 *   - wiki/music-decoder-plan.md (re-sequencer design)
 */
import { MusicPlayer, type MusicPlayerState } from './music-player'
import {
  DEFAULT_INTENSITY,
  DEFAULT_LEVEL,
  INTENSITY_LABELS,
  clampIntensity,
  isMusicLevelId,
  pickLevelSongTable,
  songForLevel,
  type LevelSongTable,
  type MusicIntensity,
  type MusicLevelId,
  type MusicSongId,
} from './music-states'

export type MusicStateChangeListener = (state: MusicPlayerState) => void

/**
 * Enemy-count cut-points used to derive an intensity from `setCombatSource()`.
 *
 * !! OURS, NOT THE ORIGINAL'S !!
 *
 * The engine climbs a ladder of float thresholds against an internal danger
 * metric to choose the intensity row; neither the metric nor the thresholds
 * have been recovered. These two integers are a stand-in so the remaster has
 * *some* intensity motion, and are the one place to change when the real
 * ladder is read out of the binary.
 *
 *   enemies < 1        -> intensity 0
 *   1 <= enemies < 3   -> intensity 1
 *   enemies >= 3       -> intensity 2
 */
export const ENEMY_COUNT_INTENSITY_CUTS: readonly [number, number] = [1, 3]

/** Map a live enemy count onto the intensity ladder. See the caveat above. */
export function intensityForEnemyCount(enemyCount: number): MusicIntensity {
  const [alert, combat] = ENEMY_COUNT_INTENSITY_CUTS
  if (enemyCount >= combat) return 2
  if (enemyCount >= alert) return 1
  return 0
}

export class MusicManager {
  private player: MusicPlayer | null = null
  private isInitialized = false
  private isStarted = false
  private listeners: Set<MusicStateChangeListener> = new Set()
  /** Optional enemy manager reference for intensity automation. */
  private combatCheck: (() => number) | null = null
  /** Optional direct intensity source, taking precedence over combatCheck. */
  private intensityCheck: (() => number) | null = null
  /** Requested level/intensity, applied to the player as soon as it exists. */
  private pendingLevel: MusicLevelId = DEFAULT_LEVEL
  private pendingIntensity: MusicIntensity = DEFAULT_INTENSITY
  /** Cached last applied intensity to avoid redundant transitions. */
  private lastAppliedIntensity: MusicIntensity = DEFAULT_INTENSITY
  /** This playthrough's level->song table, chosen once (as the original does). */
  private table: LevelSongTable = 'a'

  /**
   * Build the player for the current (level, intensity).
   *
   * Safe to call multiple times; only the first call has any effect.
   *
   * @param options.table - pin the level->song table instead of drawing one at
   *   random. The original draws once per playthrough; tests pin it.
   */
  initialize(options: { table?: LevelSongTable } = {}): void {
    if (this.isInitialized) return

    this.table = options.table ?? pickLevelSongTable()

    this.player = new MusicPlayer({
      level: this.pendingLevel,
      intensity: this.pendingIntensity,
      table: this.table,
    })

    this.lastAppliedIntensity = this.pendingIntensity
    this.isInitialized = true
  }

  /**
   * Provide a function that returns the current number of active enemies. The
   * manager will map that count onto the intensity ladder on every `tick()`.
   *
   * The mapping is `intensityForEnemyCount()`; its cut-points are our own
   * invention, not recovered from the binary.
   */
  setCombatSource(enemyCount: () => number): void {
    this.combatCheck = enemyCount
  }

  /**
   * Drive intensity from an arbitrary source instead of an enemy count — e.g.
   * a real danger metric once one exists. The value is clamped onto 0..2.
   */
  setIntensitySource(intensity: () => number): void {
    this.combatCheck = null
    this.intensityCheck = intensity
  }

  /**
   * Add a listener that fires whenever the active music selection changes.
   * Used by the HUD to display the current level/intensity.
   */
  onStateChange(listener: MusicStateChangeListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Start playback. Must be called from a user-gesture handler (click, keydown)
   * to satisfy browser autoplay policies.
   */
  async start(): Promise<void> {
    if (!this.isInitialized) this.initialize()
    if (!this.player) return
    if (this.isStarted) return

    await this.player.start()
    this.isStarted = true
    this.lastAppliedIntensity = this.player.getIntensity()
  }

  /**
   * Stop the engine and free all audio resources.
   */
  stop(): void {
    if (this.player && this.isStarted) {
      this.player.stop()
    }
    this.isStarted = false
  }

  /**
   * Enter a level (0..9). Re-resolves the song through this playthrough's
   * level->song table. No-op for an out-of-range index.
   *
   * Which of the ten indices belongs to which shipped map is still unknown, so
   * callers must pass the numeric music level index, not a map name.
   */
  setLevel(level: MusicLevelId): void {
    if (!isMusicLevelId(level)) {
      console.warn(`Ignoring out-of-range music level ${level}`)
      return
    }
    if (level === this.pendingLevel) return
    this.pendingLevel = level
    this.player?.setLevel(level)
    this._notify()
  }

  /**
   * Set the intensity row (0..2). This is the primary gameplay-facing API. The
   * manager coalesces redundant calls and notifies listeners.
   */
  setIntensity(intensity: MusicIntensity): void {
    const clamped = clampIntensity(intensity)
    if (this.lastAppliedIntensity === clamped && this.pendingIntensity === clamped) return
    this.pendingIntensity = clamped
    // Always propagate to the underlying player so getIntensity() reflects the
    // change immediately, even before start() has been called. The player's
    // own setIntensity() is a no-op for the audio path until start() runs, so
    // there is no risk of double-scheduling.
    this.player?.setIntensity(clamped)
    this.lastAppliedIntensity = clamped
    this._notify()
  }

  /**
   * Force an immediate intensity change, restarting the row from its first
   * pair (used for explicit gameplay triggers rather than the gradual ladder).
   */
  forceIntensity(intensity: MusicIntensity): void {
    const clamped = clampIntensity(intensity)
    this.pendingIntensity = clamped
    this.lastAppliedIntensity = clamped
    this.player?.forceIntensity(clamped)
    this._notify()
  }

  /**
   * Set the music volume (0.0 – 1.0).
   */
  setVolume(gain: number): void {
    if (this.player) this.player.setVolume(gain)
  }

  /** Everything identifying what is currently selected. */
  getState(): MusicPlayerState {
    if (this.player) return this.player.getState()
    const songId = songForLevel(this.pendingLevel, this.table)
    return {
      level: this.pendingLevel,
      intensity: this.pendingIntensity,
      songId,
      table: this.table,
      label: `L${this.pendingLevel} S${songId ?? '-'} ${INTENSITY_LABELS[
        this.pendingIntensity
      ].toUpperCase()}`,
    }
  }

  getLevel(): MusicLevelId {
    return this.player?.getLevel() ?? this.pendingLevel
  }

  getIntensity(): MusicIntensity {
    return this.player?.getIntensity() ?? this.pendingIntensity
  }

  getSongId(): MusicSongId | null {
    return this.player?.getSongId() ?? songForLevel(this.pendingLevel, this.table)
  }

  /** This playthrough's level->song table. */
  getSongTable(): LevelSongTable {
    return this.player?.getSongTable() ?? this.table
  }

  /**
   * True if the audio engine is running and producing sound.
   */
  get running(): boolean {
    return this.isStarted
  }

  /**
   * Poll-driven update. Should be called once per game tick. Maps the
   * registered combat/intensity source onto the intensity ladder.
   */
  tick(): void {
    if (!this.isStarted) return

    if (this.intensityCheck) {
      this.setIntensity(clampIntensity(this.intensityCheck()))
      return
    }

    if (!this.combatCheck) return
    this.setIntensity(intensityForEnemyCount(this.combatCheck()))
  }

  private _notify(): void {
    const state = this.getState()
    for (const l of this.listeners) {
      try {
        l(state)
      } catch (err) {
        console.warn('MusicManager state listener threw:', err)
      }
    }
    // Also dispatch a DOM CustomEvent so out-of-tree consumers (HUD, devtools,
    // browser extensions) can subscribe without holding a reference to the
    // MusicManager instance.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('esoteria:musicState', { detail: state }))
    }
  }
}

/**
 * Process-wide singleton. Lazy-constructed on first access.
 */
let _instance: MusicManager | null = null
export function getMusicManager(): MusicManager {
  if (!_instance) _instance = new MusicManager()
  return _instance
}
